import { Storage } from '@ionic/storage';

import { KEY_IGNORE_ENCRYP, PREFIX_IGNORE_ENCRYP } from '../../models/Constants';
import { EncryptionService } from '../encryption/encryption.service';

/**
 * @class StorageData
 * @description
 * Clase utilitaria singleton para manejar el almacenamiento local de datos
 * usando `@ionic/storage`, con soporte para cifrado AES opcional a través del `EncryptionService`.
 *
 * Esta clase static permite almacenar y recuperar información de forma segura
 * sin necesidad de inyectar servicios en cada componente o servicio que la utilice.
 *
 * Servicio para estandarizar la libreria de Storage que usa ionic.
 * El comportamiento de cifrado puede desactivarse en entornos de desarrollo mediante localhost, lo que facilita pruebas sin ocultar los valores.
 */
export class StorageData {

    private static storage: Storage;
    private static encryptionS: EncryptionService;

    /**
     * Inicializa el singleton con las instancias de `Storage` e `EncryptionService`.
     * Este método debe ser llamado una vez al inicio de la aplicación.
     * @param storage Instancia de `@ionic/storage`
     * @param encryption Instancia de `EncryptionService`
     */
    public static init(storage: Storage, encryption: EncryptionService): void {
        this.storage = storage;
        this.encryptionS = encryption;
    }

    /**
     * Determina si una clave debe ignorar el proceso de encriptación/desencriptación.
     * @param key Clave a evaluar
     */
    public static isIgnoredKey(key: string): boolean {
        if (KEY_IGNORE_ENCRYP[key]) {
            return true;
        }
        return PREFIX_IGNORE_ENCRYP?.some(prefix => key.startsWith(prefix)) ?? false;
    }

    /**
     * Guarda un valor encriptado (si aplica) en el storage local con la clave especificada.
     * @param key Clave única para identificar el dato
     * @param data Valor a almacenar (objeto, array, etc...)
     */
    public static async setDataStorage<T>(key: string, data: T): Promise<void> {
        const isLocalhost = location.hostname === 'localhost' || location.hostname.startsWith('192.168.');
        const plaintext = JSON.stringify(data);
        const ignore_key = this.isIgnoredKey(key);
        const encrypted = ignore_key || isLocalhost ? data : this.encryptionS.encrypt(plaintext);
        await this.storage.set(key, encrypted);
    }

    /**
     * Recupera un valor del storage. Si está cifrado, lo descifra antes de devolverlo.
     * Si el valor no está cifrado (migraciones), lo intenta parsear directamente.
     * @param key Clave del valor a recuperar
     * @returns Valor almacenado parseado como tipo `T`, o `null` si no existe o hay error
     */
    public static async getDataStorage<T = any>(key: string): Promise<T | null> {
        try {
            const stored = await this.storage.get(key);
            if (!stored) return null;
            const ignore_key = this.isIgnoredKey(key);
            if (ignore_key) return stored;
            if (typeof stored === 'string') {
                const decrypted = this.encryptionS.decrypt<T>(stored);
                return decrypted;
            }
            return stored;
        } catch (error) {
            return null;
        }
    }

    /**
     * Elimina un valor del storage
     * @param key Clave del valor a quitar
     */
    public static removeDataStorage(key: string) {
        return new Promise((resolve, reject) => this.storage.remove(key).then((x) => resolve(x)).catch(error => reject(error)));
    }

    /**
     * Elimina multiple valores del storage
     * @param keys Arrays de claves de los valores a quitar
     */
    public static removeDataStorageMultiple(keys: string[]): Promise<any> {
        const keyesPromises: Array<Promise<any>> = [];

        keys.forEach(element => keyesPromises.push(this.removeDataStorage(element)));

        return new Promise((resolve, reject) => {
            Promise.all(keyesPromises).then((x) => resolve(x)).catch(error => reject(error))
        });
    }

}
